<template>
  <div class="followup-root h-full min-h-screen overflow-y-auto bg-[#f8fafc] dark:bg-[#0e1015] text-slate-900 dark:text-slate-100 p-4 md:p-6 lg:p-7 space-y-6 transition-colors duration-150 animate-fade-in-up">
    
    <!-- 1. CABEÇALHO DO MÓDULO -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-3 border-b border-slate-200/80 dark:border-[#26282d]">
      <div class="flex items-center gap-3.5">
        <div class="size-12 rounded-2xl bg-blue-50 dark:bg-[#2781F6]/15 border border-blue-200/80 dark:border-[#2781F6]/30 text-[#2781F6] flex items-center justify-center shadow-xs">
          <span class="i-lucide-repeat size-6" />
        </div>
        <div>
          <h1 class="text-xl md:text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <span>Follow-up Inteligente com IA</span>
            <span class="text-xs font-bold px-2.5 py-0.5 rounded-full bg-blue-50 dark:bg-[#2781F6]/20 text-[#2781F6] border border-blue-200 dark:border-[#2781F6]/40">
              GPT-5.6 Luna & 4o
            </span>
          </h1>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Réguas automáticas de reengajamento e recuperação de clientes vinculadas aos funis de venda e WhatsApp
          </p>
        </div>
      </div>

      <!-- Ações do Cabeçalho -->
      <div class="flex items-center gap-2.5 flex-wrap">
        <button
          @click="runEngineManual"
          :disabled="isEngineRunning"
          class="flex items-center gap-1.5 bg-amber-50 dark:bg-amber-500/15 border border-amber-300/80 dark:border-amber-500/30 text-amber-700 dark:text-amber-300 text-xs font-bold py-2.5 px-3.5 rounded-xl shadow-xs hover:bg-amber-100 dark:hover:bg-amber-500/25 transition-all cursor-pointer disabled:opacity-60 active:scale-98"
        >
          <span :class="['i-lucide-zap size-3.5 text-amber-600 dark:text-amber-400', isEngineRunning ? 'animate-bounce' : '']" />
          <span>{{ isEngineRunning ? 'Avaliando Fila...' : '⚡ Forçar Avaliação / Disparo' }}</span>
        </button>

        <button
          @click="fetchData"
          :disabled="isLoading"
          class="flex items-center gap-1.5 bg-white dark:bg-[#141518] border border-slate-200 dark:border-[#26282d] text-xs font-bold text-slate-700 dark:text-slate-300 py-2.5 px-3.5 rounded-xl shadow-xs hover:bg-slate-50 dark:hover:bg-[#1a1b20] transition-all cursor-pointer disabled:opacity-60 active:scale-98"
        >
          <span :class="['i-lucide-rotate-cw size-3.5 text-slate-500 dark:text-slate-400', isLoading ? 'animate-spin text-[#2781F6]' : '']" />
          <span>Atualizar</span>
        </button>

        <button
          @click="openCreateModal"
          class="flex items-center gap-2 bg-[#2781F6] hover:bg-[#1c6edb] text-white text-xs font-black py-2.5 px-4 rounded-xl shadow-sm hover:shadow-md hover:shadow-blue-500/20 active:scale-98 transition-all cursor-pointer"
        >
          <span class="i-lucide-plus size-4" />
          <span>Nova Régua de Follow-up</span>
        </button>
      </div>
    </div>

    <!-- 2. CARDS DE MÉTRICAS KPI -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <!-- Card 1: Réguas Ativas -->
      <div class="followup-card bg-white dark:bg-[#131418] border border-slate-200/90 dark:border-[#26282d] rounded-2xl p-5 shadow-xs hover:shadow-md hover:border-[#2781F6]/50 dark:hover:border-[#2781F6]/60 dark:hover:bg-[#18191f] flex flex-col justify-between min-h-[115px]">
        <div class="flex items-center justify-between">
          <span class="text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
            RÉGUAS ATIVAS
          </span>
          <span class="p-2 rounded-xl bg-blue-50 dark:bg-[#2781F6]/15 text-[#2781F6] border border-blue-200/80 dark:border-[#2781F6]/30">
            <span class="i-lucide-git-branch size-4" />
          </span>
        </div>
        <div class="flex items-baseline gap-2 my-1">
          <span class="text-3xl font-black text-[#2781F6] tracking-tight">
            {{ activeRulesCount }}
          </span>
          <span class="text-xs font-bold text-slate-400">/ {{ rules.length }} total</span>
        </div>
        <div class="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
          Automatizações ativas no funil
        </div>
      </div>

      <!-- Card 2: Disparos Agendados -->
      <div class="followup-card bg-white dark:bg-[#131418] border border-slate-200/90 dark:border-[#26282d] rounded-2xl p-5 shadow-xs hover:shadow-md hover:border-amber-500/50 dark:hover:border-amber-500/60 dark:hover:bg-[#18191f] flex flex-col justify-between min-h-[115px]">
        <div class="flex items-center justify-between">
          <span class="text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
            DISPAROS AGENDADOS
          </span>
          <span class="p-2 rounded-xl bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-200/80 dark:border-amber-500/30">
            <span class="i-lucide-clock size-4" />
          </span>
        </div>
        <div class="text-3xl font-black text-slate-900 dark:text-white my-1 tracking-tight">
          {{ scheduledCount }}
        </div>
        <div class="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
          Próximos envios na fila
        </div>
      </div>

      <!-- Card 3: Mensagens Enviadas -->
      <div class="followup-card bg-white dark:bg-[#131418] border border-slate-200/90 dark:border-[#26282d] rounded-2xl p-5 shadow-xs hover:shadow-md hover:border-[#2781F6]/50 dark:hover:border-[#2781F6]/60 dark:hover:bg-[#18191f] flex flex-col justify-between min-h-[115px]">
        <div class="flex items-center justify-between">
          <span class="text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
            MENSAGENS ENVIADAS
          </span>
          <span class="p-2 rounded-xl bg-blue-50 dark:bg-[#2781F6]/15 text-[#2781F6] border border-blue-200/80 dark:border-[#2781F6]/30">
            <span class="i-lucide-send size-4" />
          </span>
        </div>
        <div class="text-3xl font-black text-slate-900 dark:text-white my-1 tracking-tight">
          {{ sentCount }}
        </div>
        <div class="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
          Follow-ups entregues
        </div>
      </div>

      <!-- Card 4: Leads Recuperados -->
      <div class="followup-card bg-white dark:bg-[#131418] border border-slate-200/90 dark:border-[#26282d] rounded-2xl p-5 shadow-xs hover:shadow-md hover:border-emerald-500/50 dark:hover:border-emerald-500/60 dark:hover:bg-[#18191f] flex flex-col justify-between min-h-[115px]">
        <div class="flex items-center justify-between">
          <span class="text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
            LEADS RECUPERADOS
          </span>
          <span class="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-200/80 dark:border-emerald-500/30">
            <span class="i-lucide-sparkles size-4" />
          </span>
        </div>
        <div class="text-3xl font-black text-emerald-600 dark:text-emerald-400 my-1 tracking-tight">
          {{ recoveredCount }}
        </div>
        <div class="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
          Clientes que voltaram a responder
        </div>
      </div>
    </div>

    <!-- 3. ABAS DE NAVEGAÇÃO (RÉGUAS vs HISTÓRICO) -->
    <div class="flex items-center gap-3 border-b border-slate-200/80 dark:border-[#26282d] pt-2">
      <button
        @click="activeViewTab = 'rules'"
        :class="[
          'pb-3 px-4 text-xs font-black transition-all duration-150 cursor-pointer flex items-center gap-2 border-b-2 -mb-px',
          activeViewTab === 'rules'
            ? 'border-[#2781F6] text-[#2781F6] bg-blue-50/70 dark:bg-[#2781F6]/15 rounded-t-xl'
            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
        ]"
      >
        <span class="i-lucide-layers size-4" />
        <span>Réguas de Follow-up ({{ rules.length }})</span>
      </button>

      <button
        @click="activeViewTab = 'history'"
        :class="[
          'pb-3 px-4 text-xs font-black transition-all duration-150 cursor-pointer flex items-center gap-2 border-b-2 -mb-px',
          activeViewTab === 'history'
            ? 'border-[#2781F6] text-[#2781F6] bg-blue-50/70 dark:bg-[#2781F6]/15 rounded-t-xl'
            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
        ]"
      >
        <span class="i-lucide-history size-4" />
        <span>Histórico & Execuções em Tempo Real</span>
      </button>
    </div>

    <!-- 4. VIEW: LISTA DE RÉGUAS CONFIGURADAS -->
    <div v-if="activeViewTab === 'rules'" class="space-y-4">
      <div v-if="!rules.length && !isLoading" class="p-12 text-center bg-white dark:bg-[#131418] rounded-2xl border border-slate-200 dark:border-[#26282d] space-y-3">
        <div class="size-14 rounded-2xl bg-blue-50 dark:bg-[#2781F6]/15 text-[#2781F6] flex items-center justify-center mx-auto">
          <span class="i-lucide-repeat size-7" />
        </div>
        <h3 class="text-base font-black text-slate-900 dark:text-white">Nenhuma Régua Criada Ainda</h3>
        <p class="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
          Crie sua primeira régua de follow-up automático para que a inteligência artificial recupere clientes parados nas etapas de vendas.
        </p>
        <button
          @click="openCreateModal"
          class="mt-2 bg-[#2781F6] hover:bg-[#1c6edb] text-white text-xs font-black py-2.5 px-5 rounded-xl shadow-md shadow-blue-500/20 transition-all cursor-pointer inline-flex items-center gap-2 active:scale-98"
        >
          <span class="i-lucide-plus size-4" />
          <span>Criar Primeira Régua</span>
        </button>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <div
          v-for="rule in rules"
          :key="rule.id"
          class="followup-card bg-white dark:bg-[#131418] border border-slate-200/90 dark:border-[#26282d] hover:border-[#2781F6]/50 dark:hover:border-[#2781F6]/60 dark:hover:bg-[#18191f] rounded-2xl p-5 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between space-y-4 relative group"
        >
          <!-- Topo do Card -->
          <div class="space-y-2.5">
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-1.5 flex-wrap">
                <!-- Badge Ativa / Pausada -->
                <span
                  class="text-[10px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider"
                  :class="rule.is_active ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30' : 'bg-slate-100 dark:bg-[#1a1b20] text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-[#2c2d33]'"
                >
                  {{ rule.is_active ? 'ATIVA' : 'PAUSADA' }}
                </span>

                <!-- Badge Piloto Automático / Modo Sugestão -->
                <span
                  class="text-[10px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1"
                  :class="rule.send_mode === 'automatic' ? 'bg-blue-50 dark:bg-[#2781F6]/15 text-[#2781F6] border border-blue-200 dark:border-[#2781F6]/30' : 'bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30'"
                >
                  <span :class="rule.send_mode === 'automatic' ? 'i-lucide-zap' : 'i-lucide-user-check'" class="size-3" />
                  <span>{{ rule.send_mode === 'automatic' ? 'Automático' : 'Co-piloto' }}</span>
                </span>
              </div>

              <!-- Switch Ativar/Pausar -->
              <button
                @click="toggleRuleActive(rule)"
                class="text-xs font-bold text-slate-500 hover:text-[#2781F6] cursor-pointer"
                :title="rule.is_active ? 'Clique para pausar régua' : 'Clique para ativar régua'"
              >
                <span :class="rule.is_active ? 'i-lucide-pause-circle size-4 text-emerald-500' : 'i-lucide-play-circle size-4 text-slate-400'" />
              </button>
            </div>

            <!-- Título da Régua -->
            <h3 class="text-sm font-black text-slate-900 dark:text-white line-clamp-1 group-hover:text-[#2781F6] transition-colors">
              {{ rule.name }}
            </h3>

            <!-- Funil e Etapa Alvo -->
            <div class="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 flex-wrap">
              <span class="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-[#1a1b20] font-semibold border border-slate-200 dark:border-[#26282d]">
                {{ getFunnelDisplayName(rule.funnel_attribute_key) }}
              </span>
              <span class="text-slate-400">➔</span>
              <span class="px-2 py-0.5 rounded-md bg-blue-50 dark:bg-[#2781F6]/10 text-[#2781F6] font-bold border border-blue-100 dark:border-[#2781F6]/20">
                {{ rule.stage_name }}
              </span>
            </div>

            <!-- Tags Obrigatórias e Exclusões Resumidas -->
            <div v-if="(rule.include_labels && rule.include_labels.length) || (rule.exclude_labels && rule.exclude_labels.length)" class="flex flex-wrap gap-1 pt-1">
              <span
                v-for="tag in (rule.include_labels || []).slice(0, 3)"
                :key="'inc-'+tag"
                class="px-1.5 py-0.2 rounded text-[10px] font-semibold bg-[#2781F6]/10 text-[#2781F6] border border-[#2781F6]/20"
              >
                +{{ tag }}
              </span>
              <span v-if="(rule.include_labels || []).length > 3" class="text-[10px] text-slate-400 self-center">
                +{{ rule.include_labels.length - 3 }} mais
              </span>

              <span
                v-for="tag in (rule.exclude_labels || []).slice(0, 2)"
                :key="'exc-'+tag"
                class="px-1.5 py-0.2 rounded text-[10px] font-semibold bg-rose-50 dark:bg-rose-950/30 text-rose-500 border border-rose-200 dark:border-rose-900/40"
              >
                -{{ tag }}
              </span>
            </div>

            <!-- Cadência em Pills -->
            <div class="flex items-center gap-1.5 flex-wrap pt-1">
              <span
                v-for="(step, sIdx) in (rule.cadence_steps || [])"
                :key="sIdx"
                class="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-[#1a1b20] text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-[#26282d] flex items-center gap-1"
              >
                <span>T{{ step.step }}:</span>
                <span class="text-[#2781F6]">{{ formatDelay(step.delay_hours) }}</span>
              </span>
            </div>
          </div>

          <!-- Rodapé do Card -->
          <div class="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-[#26282d] text-xs">
            <div class="flex items-center gap-1.5 text-slate-400">
              <span class="i-lucide-sparkles size-3 text-[#2781F6]" />
              <span class="text-[11px] font-medium">{{ rule.ai_model || 'GPT-5.6' }}</span>
            </div>

            <div class="flex items-center gap-1">
              <button
                @click="editRule(rule)"
                class="p-1.5 rounded-lg text-slate-600 dark:text-slate-300 hover:text-[#2781F6] hover:bg-blue-50 dark:hover:bg-[#2781F6]/10 transition-colors cursor-pointer"
                title="Editar Régua"
              >
                <span class="i-lucide-pencil size-3.5" />
              </button>

              <button
                @click="deleteRule(rule)"
                class="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-colors cursor-pointer"
                title="Excluir Régua"
              >
                <span class="i-lucide-trash-2 size-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 5. VIEW: HISTÓRICO & EXECUÇÕES AO VIVO -->
    <div v-if="activeViewTab === 'history'" class="bg-white dark:bg-[#131418] border border-slate-200/90 dark:border-[#26282d] rounded-2xl shadow-xs overflow-hidden">
      <div class="p-4 border-b border-slate-200 dark:border-[#26282d] flex items-center justify-between flex-wrap gap-3">
        <div class="flex items-center gap-2">
          <span class="i-lucide-activity size-4 text-[#2781F6]" />
          <h3 class="text-sm font-black text-slate-900 dark:text-white">Fila de Execuções e Disparos em Tempo Real</h3>
        </div>

        <!-- Filtros de Status -->
        <div class="flex items-center gap-1.5 flex-wrap">
          <button
            v-for="filter in statusFilters"
            :key="filter.id"
            @click="selectedStatusFilter = filter.id; fetchHistory()"
            :class="[
              'px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer border',
              selectedStatusFilter === filter.id
                ? 'bg-[#2781F6] text-white border-[#2781F6]'
                : 'bg-slate-50 dark:bg-[#1a1b20] text-slate-600 dark:text-slate-400 border-slate-200 dark:border-[#26282d] hover:bg-slate-100 dark:hover:bg-[#22242a]'
            ]"
          >
            {{ filter.label }}
          </button>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs text-slate-600 dark:text-slate-300">
          <thead class="bg-slate-50 dark:bg-[#1a1b20] text-[10px] font-black uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-[#26282d] tracking-wider">
            <tr>
              <th class="py-3 px-4">Lead / Conversa</th>
              <th class="py-3 px-4">Régua</th>
              <th class="py-3 px-4">Tentativa</th>
              <th class="py-3 px-4">Status</th>
              <th class="py-3 px-4">Data / Horário</th>
              <th class="py-3 px-4">Mensagem / Observação</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-[#26282d]">
            <tr v-for="exec in executions" :key="exec.id" class="hover:bg-slate-50/80 dark:hover:bg-[#1a1b20] transition-colors duration-150">
              <td class="py-3 px-4 font-bold text-slate-900 dark:text-white">
                <div class="flex flex-col">
                  <a
                    :href="`/app/accounts/${currentAccountId}/conversations/${exec.conversation_id}`"
                    target="_blank"
                    class="hover:text-[#2781F6] hover:underline flex items-center gap-1"
                  >
                    <span>{{ exec.contact_name }}</span>
                    <span class="i-lucide-external-link size-3 text-slate-400" />
                  </a>
                  <span class="text-[11px] text-slate-400 font-mono">{{ exec.contact_phone || `#${exec.conversation_id}` }}</span>
                </div>
              </td>
              <td class="py-3 px-4 font-medium">{{ exec.rule_name }}</td>
              <td class="py-3 px-4">
                <span class="px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 dark:bg-[#2781F6]/15 text-[#2781F6] border border-blue-200 dark:border-[#2781F6]/30">
                  Passo {{ exec.step_number }}
                </span>
              </td>
              <td class="py-3 px-4">
                <span
                  class="px-2.5 py-1 rounded-full text-[10px] font-bold inline-flex items-center gap-1 border"
                  :class="getStatusBadgeClass(exec.status)"
                >
                  <span class="size-1.5 rounded-full bg-current" />
                  <span>{{ getStatusLabel(exec.status) }}</span>
                </span>
              </td>
              <td class="py-3 px-4 text-slate-500 whitespace-nowrap font-mono text-[11px]">
                <div v-if="exec.status === 'sent' && exec.sent_at" class="text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                  <span class="i-lucide-check-circle size-3" />
                  <span>Enviado: {{ exec.sent_at }}</span>
                </div>
                <div v-else-if="exec.status === 'scheduled' && exec.scheduled_for" class="text-amber-600 dark:text-amber-400 font-bold flex items-center gap-1">
                  <span class="i-lucide-clock size-3" />
                  <span>Agendado: {{ exec.scheduled_for }}</span>
                </div>
                <div v-else class="text-slate-400">
                  {{ exec.scheduled_for || exec.sent_at || exec.created_at }}
                </div>
              </td>
              <td class="py-3 px-4 max-w-xs truncate" :title="exec.message_content || exec.cancellation_reason || exec.error_message">
                <span v-if="exec.message_content" class="text-slate-700 dark:text-slate-300">
                  {{ exec.message_content }}
                </span>
                <span v-else-if="exec.cancellation_reason" class="text-slate-500 italic text-[11px]">
                  {{ exec.cancellation_reason }}
                </span>
                <span v-else-if="exec.error_message" class="text-rose-500 italic text-[11px]">
                  Erro: {{ exec.error_message }}
                </span>
                <span v-else class="text-slate-400 italic">
                  Aguardando geração na hora do disparo
                </span>
              </td>
            </tr>
            <tr v-if="!executions.length">
              <td colspan="6" class="py-8 text-center text-slate-400 dark:text-slate-500">
                Nenhuma execução registrada com esse filtro.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 6. MODAL CONSTRUTOR DE RÉGUA -->
    <FollowUpRuleModal
      v-if="isModalOpen"
      :rule="editingRule"
      :available-models="availableModels"
      :inboxes="inboxes"
      :labels="labels"
      @close="closeModal"
      @saved="onRuleSaved"
    />

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import FollowUpsAPI from 'dashboard/api/followUps';
import FollowUpRuleModal from './components/FollowUpRuleModal.vue';

const store = useStore();
const isLoading = ref(false);
const activeViewTab = ref('rules');
const isModalOpen = ref(false);
const editingRule = ref(null);

const rules = ref([]);
const executions = ref([]);
const metrics = ref({
  total_rules: 0,
  active_rules: 0,
  scheduled_executions: 0,
  sent_messages: 0,
  recovered_leads: 0
});
const inboxes = ref([]);
const labels = ref([]);
const availableModels = ref([
  'gpt-5.6-luna',
  'gpt-4o',
  'gpt-4o-mini',
  'gpt-4-turbo',
  'gpt-3.5-turbo'
]);

const selectedStatusFilter = ref('');
const statusFilters = [
  { id: '', label: 'Todos (100 Recentes)' },
  { id: 'scheduled', label: 'Próximos Agendados' },
  { id: 'sent', label: 'Enviados' },
  { id: 'cancelled_by_reply', label: 'Lead Respondeu' }
];

const currentAccountId = computed(() => store.getters['getCurrentAccountId'] || 3);
const activeRulesCount = computed(() => metrics.value.active_rules ?? rules.value.filter(r => r.is_active).length);
const scheduledCount = computed(() => metrics.value.scheduled_executions ?? 0);
const sentCount = computed(() => metrics.value.sent_messages ?? 0);
const recoveredCount = computed(() => metrics.value.recovered_leads ?? 0);

const customAttributesList = computed(() => store.getters['attributes/getAttributesByModel']('conversation_attribute') || []);

const getFunnelDisplayName = (key) => {
  const attr = customAttributesList.value.find(a => a.attribute_key === key);
  return attr?.attribute_display_name || key || 'Geral';
};

const formatDelay = (hours) => {
  if (!hours) return 'Imediato';
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  const rem = hours % 24;
  return rem > 0 ? `${days}d ${rem}h` : `${days}d`;
};

const isEngineRunning = ref(false);

const runEngineManual = async () => {
  isEngineRunning.value = true;
  try {
    const res = await FollowUpsAPI.runEngineNow();
    if (res?.data) {
      alert(res.data.message || 'Avaliação de Follow-up executada!');
      await fetchData();
    }
  } catch (err) {
    console.error('Run engine failed:', err);
    alert('Erro ao executar motor de follow-up: ' + (err.response?.data?.error || err.message));
  } finally {
    isEngineRunning.value = false;
  }
};

const getStatusLabel = (st) => {
  const map = {
    scheduled: 'Agendado',
    processing: 'Enviando',
    sent: 'Enviado',
    suggested: 'Sugerido',
    cancelled_by_reply: 'Lead Respondeu',
    cancelled_by_stage_change: 'Mudou Etapa',
    cancelled_by_human_message: 'Atendido Manual',
    cancelled_by_rule_disabled: 'Pausado',
    cancelled_recent_message: 'Mensagem Recente',
    cancelled_duplicate: 'Duplicidade',
    cancelled_by_label: 'Etiqueta Excluída',
    failed: 'Falhou'
  };
  return map[st] || st;
};

const getStatusBadgeClass = (st) => {
  if (st === 'sent' || st === 'suggested') return 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/40';
  if (st === 'cancelled_by_reply') return 'bg-blue-50 dark:bg-[#2781F6]/15 text-[#2781F6] border border-blue-200 dark:border-[#2781F6]/40';
  if (st === 'scheduled') return 'bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/40';
  return 'bg-slate-100 dark:bg-[#1a1b20] text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-[#2c2d33]';
};

const fetchHistory = async () => {
  try {
    const params = selectedStatusFilter.value ? { status: selectedStatusFilter.value } : {};
    const histRes = await FollowUpsAPI.getExecutionsHistory(params).catch(() => ({ data: [] }));
    if (histRes?.data) {
      executions.value = histRes.data.executions || histRes.data || [];
    }
  } catch (error) {
    console.error('Failed to fetch history:', error);
  }
};

const fetchData = async () => {
  isLoading.value = true;
  try {
    const [rulesRes, histRes] = await Promise.all([
      FollowUpsAPI.getRules(),
      FollowUpsAPI.getExecutionsHistory(selectedStatusFilter.value ? { status: selectedStatusFilter.value } : {}).catch(() => ({ data: [] }))
    ]);

    if (rulesRes?.data) {
      rules.value = rulesRes.data.rules || [];
      if (rulesRes.data.metrics) {
        metrics.value = rulesRes.data.metrics;
      }
      if (rulesRes.data.available_models?.length) {
        availableModels.value = rulesRes.data.available_models;
      }
      if (rulesRes.data.inboxes?.length) {
        inboxes.value = rulesRes.data.inboxes;
      }
      if (rulesRes.data.labels?.length) {
        labels.value = rulesRes.data.labels;
      }
    }

    if (histRes?.data) {
      executions.value = histRes.data.executions || histRes.data || [];
    }
  } catch (error) {
    console.error('Failed to fetch follow-ups:', error);
  } finally {
    isLoading.value = false;
  }
};

const toggleRuleActive = async (rule) => {
  try {
    const res = await FollowUpsAPI.toggleActive(rule.id);
    if (res?.data) {
      rule.is_active = res.data.is_active;
      fetchData();
    }
  } catch (err) {
    console.error('Toggle rule failed:', err);
  }
};

const openCreateModal = () => {
  editingRule.value = null;
  isModalOpen.value = true;
};

const editRule = (rule) => {
  editingRule.value = rule;
  isModalOpen.value = true;
};

const deleteRule = async (rule) => {
  if (confirm(`Deseja realmente excluir a régua "${rule.name}"?`)) {
    try {
      await FollowUpsAPI.deleteRule(rule.id);
      rules.value = rules.value.filter(r => r.id !== rule.id);
      fetchData();
    } catch (err) {
      console.error('Delete rule failed:', err);
    }
  }
};

const closeModal = () => {
  isModalOpen.value = false;
  editingRule.value = null;
};

const onRuleSaved = () => {
  closeModal();
  fetchData();
};

onMounted(() => {
  store.dispatch('attributes/get', 0);
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  fetchData();
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

.followup-root {
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

@keyframes followupFadeInUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: followupFadeInUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.followup-card {
  transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.followup-card:hover {
  transform: translateY(-2px);
}
</style>
